import React from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Button
} from '@mui/material';
import {
  Person as PersonIcon,
  FitnessCenter as GymIcon,
  AccountBalance as BankIcon,
  Notifications as NotificationsIcon,
  VpnKey as ApiKeyIcon,
  Security as SecurityIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface SettingCard {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  color: string;
}

export const SettingsPage: React.FC = () => {
  const navigate = useNavigate();

  const settingCards: SettingCard[] = [
    {
      title: 'Profile',
      description: 'Manage your personal information and account details',
      icon: <PersonIcon sx={{ fontSize: 40 }} />,
      path: '/settings/profile',
      color: '#14B8A6'
    },
    {
      title: 'Gym Details',
      description: 'Update gym information, amenities, and operating hours',
      icon: <GymIcon sx={{ fontSize: 40 }} />,
      path: '/settings/gym-details',
      color: '#F59E0B'
    },
    {
      title: 'Bank Accounts',
      description: 'Manage bank accounts for payouts and settlements',
      icon: <BankIcon sx={{ fontSize: 40 }} />,
      path: '/settings/bank-accounts',
      color: '#8B5CF6'
    },
    {
      title: 'Notifications',
      description: 'Configure notification preferences and quiet hours',
      icon: <NotificationsIcon sx={{ fontSize: 40 }} />,
      path: '/settings/notifications',
      color: '#10B981'
    },
    {
      title: 'API Keys',
      description: 'Generate and manage API keys for integrations',
      icon: <ApiKeyIcon sx={{ fontSize: 40 }} />,
      path: '/settings/api-keys',
      color: '#3B82F6'
    },
    {
      title: 'Security',
      description: 'Password, 2FA, and security settings',
      icon: <SecurityIcon sx={{ fontSize: 40 }} />,
      path: '/settings/security',
      color: '#EF4444'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Settings
        </Typography>
        <Typography variant="body2" sx={{ color: '#666' }}>
          Manage your account, gym, and preferences
        </Typography>
      </Box>

      {/* Settings Cards */}
      <Grid container spacing={3}>
        {settingCards.map((setting) => (
          <Grid item xs={12} sm={6} md={4} key={setting.path}>
            <Card
              sx={{
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}
              onClick={() => navigate(setting.path)}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: 2,
                    backgroundColor: `${setting.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2
                  }}
                >
                  <Box sx={{ color: setting.color }}>
                    {setting.icon}
                  </Box>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {setting.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                  {setting.description}
                </Typography>
                <Button
                  endIcon={<ArrowForwardIcon />}
                  sx={{ color: setting.color, p: 0 }}
                >
                  Manage
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default SettingsPage;
